/**
 * 全局时间管理器
 *
 * @description
 * 提供高性能的时间管理功能，减少重复的时间计算开销。
 * 使用时间池化技术，在每帧开始时统一计算时间，避免多次调用performance.now()。
 *
 * @example
 * ```typescript
 * // 在游戏主循环开始时更新时间
 * TimeManager.updateFrame();
 *
 * // 获取当前时间（无额外计算开销）
 * const currentTime = TimeManager.getCurrentTime();
 * const deltaTime = TimeManager.getDeltaTime();
 *
 * // 配置时间管理器
 * TimeManager.configure({
 *   maxDeltaTime: 0.1,
 *   timeScale: 1.0,
 *   useHighPrecision: true
 * });
 * ```
 */
export class TimeManager {
    /**
     * 配置时间管理器
     * @param config 配置选项
     */
    static configure(config) {
        if (config.maxDeltaTime !== undefined) {
            this._maxDeltaTime = Math.max(0.001, config.maxDeltaTime);
        }
        if (config.timeScale !== undefined) {
            this._timeScale = Math.max(0, config.timeScale);
        }
        if (config.useHighPrecision !== undefined) {
            this._useHighPrecision = config.useHighPrecision;
        }
    }
    /**
     * 初始化时间管理器
     */
    static initialize() {
        if (this._initialized) {
            return;
        }
        const now = this._getSystemTime();
        this._startTime = now;
        this._currentTime = 0;
        this._lastTime = 0;
        this._deltaTime = 0;
        this._unscaledDeltaTime = 0;
        this._frameCount = 0;
        this._initialized = true;
    }
    /**
     * 更新帧时间（应在每帧开始时调用）
     * @param externalDeltaTime 可选的外部提供的时间差
     */
    static updateFrame(externalDeltaTime) {
        if (!this._initialized) {
            this.initialize();
        }
        this._frameCount++;
        if (externalDeltaTime !== undefined) {
            // 使用外部提供的时间差
            this._unscaledDeltaTime = Math.max(0, externalDeltaTime);
        }
        else {
            // 计算时间差
            const systemTime = this._getSystemTime();
            const currentSystemTime = (systemTime - this._startTime) / 1000;
            if (this._frameCount === 1) {
                // 第一帧，设置初始时间
                this._lastTime = currentSystemTime;
                this._unscaledDeltaTime = 0;
            }
            else {
                this._unscaledDeltaTime = currentSystemTime - this._lastTime;
            }
        }
        // 限制最大时间差，防止时间跳跃
        this._unscaledDeltaTime = Math.min(this._unscaledDeltaTime, this._maxDeltaTime);
        // 应用时间缩放
        this._deltaTime = this._unscaledDeltaTime * this._timeScale;
        // 更新当前时间
        this._lastTime = this._currentTime;
        this._currentTime += this._deltaTime;
        // 触发时间更新回调
        this._triggerUpdateCallbacks();
    }
    /**
     * 获取系统时间（毫秒）
     */
    static _getSystemTime() {
        return this._useHighPrecision ? performance.now() : Date.now();
    }
    /**
     * 触发时间更新回调
     */
    static _triggerUpdateCallbacks() {
        for (let i = 0; i < this._updateCallbacks.length; i++) {
            try {
                this._updateCallbacks[i](this._deltaTime);
            }
            catch (error) {
                console.error('时间更新回调执行失败:', error);
            }
        }
    }
    /**
     * 获取当前时间（秒）
     * @returns 从初始化开始的累计时间
     */
    static getCurrentTime() {
        return this._currentTime;
    }
    /**
     * 获取帧间时间差（秒）
     * @returns 当前帧与上一帧的时间差
     */
    static getDeltaTime() {
        return this._deltaTime;
    }
    /**
     * 获取未缩放的帧间时间差（秒）
     * @returns 未应用时间缩放的帧间时间差
     */
    static getUnscaledDeltaTime() {
        return this._unscaledDeltaTime;
    }
    /**
     * 获取时间缩放比例
     */
    static getTimeScale() {
        return this._timeScale;
    }
    /**
     * 设置时间缩放比例
     * @param scale 缩放比例，0表示暂停，1表示正常速度
     */
    static setTimeScale(scale) {
        this._timeScale = Math.max(0, scale);
    }
    /**
     * 获取帧计数
     */
    static getFrameCount() {
        return this._frameCount;
    }
    /**
     * 获取平均帧率
     */
    static getAverageFPS() {
        if (this._currentTime <= 0) {
            return 0;
        }
        return this._frameCount / this._currentTime;
    }
    /**
     * 获取当前帧率
     */
    static getCurrentFPS() {
        if (this._deltaTime <= 0) {
            return 0;
        }
        return 1 / this._unscaledDeltaTime;
    }
    /**
     * 添加时间更新回调
     * @param callback 回调函数
     */
    static addUpdateCallback(callback) {
        if (this._updateCallbacks.indexOf(callback) === -1) {
            this._updateCallbacks.push(callback);
        }
    }
    /**
     * 移除时间更新回调
     * @param callback 要移除的回调函数
     */
    static removeUpdateCallback(callback) {
        const index = this._updateCallbacks.indexOf(callback);
        if (index !== -1) {
            this._updateCallbacks.splice(index, 1);
        }
    }
    /**
     * 清除所有时间更新回调
     */
    static clearUpdateCallbacks() {
        this._updateCallbacks.length = 0;
    }
    /**
     * 重置时间管理器
     */
    static reset() {
        this._initialized = false;
        this._frameCount = 0;
        this._currentTime = 0;
        this._lastTime = 0;
        this._deltaTime = 0;
        this._unscaledDeltaTime = 0;
        this.clearUpdateCallbacks();
    }
    /**
     * 获取时间管理器统计信息
     */
    static getStats() {
        return {
            currentTime: this._currentTime,
            deltaTime: this._deltaTime,
            unscaledDeltaTime: this._unscaledDeltaTime,
            timeScale: this._timeScale,
            frameCount: this._frameCount,
            averageFPS: this.getAverageFPS(),
            currentFPS: this.getCurrentFPS(),
            maxDeltaTime: this._maxDeltaTime,
            useHighPrecision: this._useHighPrecision
        };
    }
}
/** 当前时间（秒） */
TimeManager._currentTime = 0;
/** 上一帧时间（秒） */
TimeManager._lastTime = 0;
/** 帧间时间差（秒） */
TimeManager._deltaTime = 0;
/** 未缩放的帧间时间差（秒） */
TimeManager._unscaledDeltaTime = 0;
/** 时间缩放比例 */
TimeManager._timeScale = 1.0;
/** 最大允许的帧间时间差（防止时间跳跃） */
TimeManager._maxDeltaTime = 0.1;
/** 是否使用高精度时间 */
TimeManager._useHighPrecision = true;
/** 是否已初始化 */
TimeManager._initialized = false;
/** 帧计数器 */
TimeManager._frameCount = 0;
/** 启动时间 */
TimeManager._startTime = 0;
/** 时间更新回调列表 */
TimeManager._updateCallbacks = [];
//# sourceMappingURL=TimeManager.js.map